/**
 * JobTimelinePage — Search a job by number or ID, then display its full ETA timeline track.
 *
 * Uses GET /api/v1/platform/tenants/:tid/schedule/job/:jobId/timeline
 * to load all ETA-offset `scheduled_jobs` entries for a specific business job.
 *
 * Renders a vertical timeline with:
 *  - Colored nodes (pending=blue, triggered=green, overdue=red, cancelled=grey)
 *  - Rule ID & intent description per node
 *  - Days-offset label (e.g. "ETA-3", "ETA+0", "ETA+4")
 *  - Exact run_at datetime
 *  - Clicking a node shows full params JSON in an inline detail panel
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { GitBranch, Search, Clock, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TimelineEntry {
  id: string;
  name: string;          // "timeline:jobId:ruleId"
  skill: string;
  description: string;
  run_at: string | null;
  run_count: number;
  is_active: boolean;
  params: unknown;
  created_at: string;
}

type EntryStatus = 'pending' | 'triggered' | 'cancelled' | 'overdue';

function getEntryStatus(entry: TimelineEntry): EntryStatus {
  if (!entry.is_active && entry.run_count === 0) return 'cancelled';
  if (entry.run_count > 0) return 'triggered';
  if (entry.run_at && new Date(entry.run_at) < new Date()) return 'overdue';
  return 'pending';
}

function parseRuleId(name: string): string {
  const parts = name.split(':');
  return parts.length >= 3 ? parts.slice(2).join(':') : name;
}

function getOffsetLabel(ruleId: string): string {
  // Parse offset from common rule naming: si-ams-check (eta-7), ct-free-day-warn (eta+3), etc.
  const offsets: Record<string, string> = {
    'si-ams-check':    'ETA-7',
    'si-permit':       'ETA-3',
    'si-cfs-slot':     'ETA-1',
    'si-arrival-chk':  'ETA+0',
    'si-demurrage':    'ETA+4',
    'ct-eta-verify':   'ETA-2',
    'ct-assign-trip':  'ETA+0',
    'ct-free-day-warn':'ETA+3',
  };
  return offsets[ruleId] ?? '';
}

function getRuleName(ruleId: string): string {
  const names: Record<string, string> = {
    'si-ams-check':    'AMS 申报检查',
    'si-permit':       '进口许可证申请',
    'si-cfs-slot':     'CFS 仓位预定',
    'si-arrival-chk':  '船舶到港确认',
    'si-demurrage':    '超期滞港预警',
    'ct-eta-verify':   'ETA 核实提醒',
    'ct-assign-trip':  '安排提货行程',
    'ct-free-day-warn':'免费期到期提醒',
  };
  return names[ruleId] ?? ruleId;
}

const STATUS_CONFIG: Record<EntryStatus, {
  label: string;
  colorClass: string;
  dotClass: string;
  lineClass: string;
  icon: typeof Clock;
}> = {
  pending: {
    label: '等待中',
    colorClass: 'text-blue-700',
    dotClass: 'bg-blue-500 ring-blue-100',
    lineClass: 'bg-blue-200',
    icon: Clock,
  },
  overdue: {
    label: '已超时',
    colorClass: 'text-red-700',
    dotClass: 'bg-red-500 ring-red-100',
    lineClass: 'bg-red-200',
    icon: AlertTriangle,
  },
  triggered: {
    label: '已触发',
    colorClass: 'text-green-700',
    dotClass: 'bg-green-500 ring-green-100',
    lineClass: 'bg-green-200',
    icon: CheckCircle2,
  },
  cancelled: {
    label: '已取消',
    colorClass: 'text-gray-400',
    dotClass: 'bg-gray-300 ring-gray-100',
    lineClass: 'bg-gray-100',
    icon: XCircle,
  },
};

function formatRunAt(runAt: string | null): string {
  if (!runAt) return '—';
  return new Date(runAt).toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function getRelativeRunAt(runAt: string | null): string {
  if (!runAt) return '';
  const now = Date.now();
  const ts = new Date(runAt).getTime();
  const diffMs = ts - now;
  const diffH = diffMs / 1000 / 3600;
  if (Math.abs(diffH) < 1) return `${Math.round(Math.abs(diffMs) / 60000)}分钟${diffMs < 0 ? '前' : '后'}`;
  if (Math.abs(diffH) < 24) return `${Math.round(Math.abs(diffH))}小时${diffMs < 0 ? '前' : '后'}`;
  return `${Math.round(Math.abs(diffH) / 24)}天${diffMs < 0 ? '前' : '后'}`;
}

export default function JobTimelinePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const selectedTenantId = searchParams.get('tenantId') ?? '';

  const [jobIdInput, setJobIdInput] = useState('');
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchedJobId, setSearchedJobId] = useState<string>('');

  const handleSearch = useCallback(async () => {
    const jobId = jobIdInput.trim();
    if (!jobId || !selectedTenantId) return;
    setLoading(true);
    setError(null);
    setSearched(false);
    setExpandedId(null);
    try {
      const res = await apiFetch<TimelineEntry[]>(
        `/platform/tenants/${selectedTenantId}/schedule/job/${encodeURIComponent(jobId)}/timeline`
      );
      setEntries(Array.isArray(res) ? res : []);
      setSearchedJobId(jobId);
      setSearched(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [jobIdInput, selectedTenantId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') void handleSearch();
  };

  const completedCount = entries.filter(e => getEntryStatus(e) === 'triggered').length;
  const pendingCount = entries.filter(e => ['pending', 'overdue'].includes(getEntryStatus(e))).length;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <GitBranch className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold">{t('admin.jobTimeline', 'Job ETA 时间轴')}</h1>
            <p className="text-xs text-muted-foreground">
              {selectedTenantId ? `查看某个 Job 的 ETA 触发计划` : '请在 URL 中指定?tenantId='}
            </p>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-6 py-4 border-b border-border bg-muted/20 shrink-0">
        <div className="flex gap-2 max-w-lg">
          <Input
            placeholder="输入 Job ID 或 Job Number (如: JOB-2025-001)"
            value={jobIdInput}
            onChange={e => setJobIdInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="text-sm h-9"
            disabled={!selectedTenantId}
          />
          <Button
            onClick={() => void handleSearch()}
            disabled={!jobIdInput.trim() || !selectedTenantId || loading}
            className="h-9 px-4"
            size="sm"
          >
            <Search className="h-4 w-4 mr-1.5" />
            {loading ? '查询中...' : '查询'}
          </Button>
        </div>
        {!selectedTenantId && (
          <p className="mt-2 text-xs text-muted-foreground">请先在顶部选择租户</p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Empty state */}
      {searched && entries.length === 0 && !error && (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center text-muted-foreground">
            <GitBranch className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">未找到时间轴记录</p>
            <p className="text-xs mt-1">Job ID: <span className="font-mono">{searchedJobId}</span></p>
            <p className="text-xs text-muted-foreground/70 mt-0.5">该 Job 可能尚未创建 ETA 时间轴，或 ID 不正确</p>
          </div>
        </div>
      )}

      {/* Default hint */}
      {!searched && !loading && (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Search className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">输入 Job ID 查看其 ETA 触发时间轴</p>
            <p className="text-xs mt-1 opacity-60">系统将根据 ETA 自动生成 8 个预设任务节点</p>
          </div>
        </div>
      )}

      {/* Timeline */}
      {entries.length > 0 && (
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Summary bar */}
          <div className="flex items-center gap-4 mb-5 p-3 rounded-lg bg-muted/30 border border-border">
            <div className="text-xs">
              <span className="text-muted-foreground">Job: </span>
              <span className="font-mono font-medium">{searchedJobId}</span>
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="text-xs">
              <span className="text-green-600 font-medium">{completedCount}</span>
              <span className="text-muted-foreground ml-1">已触发</span>
            </div>
            <div className="text-xs">
              <span className="text-blue-600 font-medium">{pendingCount}</span>
              <span className="text-muted-foreground ml-1">待执行</span>
            </div>
            <div className="text-xs">
              <span className="text-gray-400 font-medium">{entries.filter(e => !e.is_active && e.run_count === 0).length}</span>
              <span className="text-muted-foreground ml-1">已取消</span>
            </div>
            <div className="text-xs ml-auto text-muted-foreground">
              共 {entries.length} 个节点
            </div>
          </div>

          {/* Vertical timeline */}
          <div className="relative">
            {entries.map((entry, idx) => {
              const status = getEntryStatus(entry);
              const cfg = STATUS_CONFIG[status];
              const ruleId = parseRuleId(entry.name);
              const ruleName = getRuleName(ruleId);
              const offsetLabel = getOffsetLabel(ruleId);
              const isLast = idx === entries.length - 1;
              const isExpanded = expandedId === entry.id;
              const StatusIcon = cfg.icon;

              return (
                <div key={entry.id} className="relative flex gap-4 pb-6">
                  {/* Vertical connector line */}
                  {!isLast && (
                    <div
                      className={cn(
                        'absolute left-3.5 top-7 bottom-0 w-0.5',
                        cfg.lineClass
                      )}
                    />
                  )}

                  {/* Dot */}
                  <div className="relative shrink-0 mt-0.5">
                    <div
                      className={cn(
                        'h-7 w-7 rounded-full ring-4 flex items-center justify-center',
                        cfg.dotClass
                      )}
                    >
                      <StatusIcon className="h-3.5 w-3.5 text-white" />
                    </div>
                    {offsetLabel && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-mono font-semibold text-muted-foreground whitespace-nowrap">
                        {offsetLabel}
                      </div>
                    )}
                  </div>

                  {/* Content card */}
                  <div className="flex-1 min-w-0">
                    <div
                      className={cn(
                        'rounded-lg border border-border bg-background p-3 cursor-pointer hover:bg-muted/20 transition-colors',
                        isExpanded && 'bg-muted/10'
                      )}
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={cn('text-xs font-semibold', cfg.colorClass)}>
                              {ruleName}
                            </span>
                            <span className={cn(
                              'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium',
                              status === 'pending' && 'bg-blue-100 text-blue-700',
                              status === 'overdue' && 'bg-red-100 text-red-700',
                              status === 'triggered' && 'bg-green-100 text-green-700',
                              status === 'cancelled' && 'bg-gray-100 text-gray-500',
                            )}>
                              {cfg.label}
                            </span>
                          </div>

                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {entry.description || ruleId}
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <div className={cn(
                            'text-xs font-medium',
                            status === 'overdue' ? 'text-red-600' :
                            status === 'triggered' ? 'text-green-600' :
                            'text-foreground'
                          )}>
                            {formatRunAt(entry.run_at)}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {getRelativeRunAt(entry.run_at)}
                          </div>
                        </div>

                        <div className="shrink-0 mt-0.5">
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          }
                        </div>
                      </div>

                      {/* Expanded params */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <div className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                            执行参数
                          </div>
                          <pre className="text-[10px] text-foreground whitespace-pre-wrap leading-snug max-h-36 overflow-y-auto bg-muted/50 rounded-md p-2">
                            {JSON.stringify(entry.params, null, 2)}
                          </pre>
                          <div className="mt-2 flex gap-4 text-[10px] text-muted-foreground">
                            <span>规则 ID: <span className="font-mono">{ruleId}</span></span>
                            <span>创建于: {new Date(entry.created_at).toLocaleString()}</span>
                            {entry.run_count > 0 && <span className="text-green-600">已触发 {entry.run_count} 次</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
